import { cancelOnSessionChange } from '../session-cancellation';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { mergeMap, map, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { OrganizationsService } from '@api/services/organizations.service';
import * as OrganizationsActions from '../../actions/organizations/core.actions';
import { ApiDefOrganizationData } from '@api/models/api-def-organization-data';

@Injectable()
export class OrganizationsCoreEffects {
  loadOrganizations$;
  loadOrganizationById$;
  createOrganization$;
  updateOrganization$;
  deleteOrganization$;

  constructor(
      private actions$: Actions,
      private organizationsService: OrganizationsService,
  ) {

    this.loadOrganizations$ = createEffect(() =>
      this.actions$.pipe(
        ofType(OrganizationsActions.loadOrganizations),
        mergeMap(() =>
          this.organizationsService.organizationsGetAllByUserId().pipe(
            map((orgs) =>
              OrganizationsActions.loadOrganizationsSuccess({
                organizations: orgs.map((org) => ({ data: org, loadedAt: new Date() }))
              })
            ),
            catchError((error) => of(OrganizationsActions.loadOrganizationsFailure({ error }))),
            cancelOnSessionChange(this.actions$)
          )
        )
      )
    );

    this.loadOrganizationById$ = createEffect(() =>
      this.actions$.pipe(
        ofType(OrganizationsActions.loadOrganizationById),
        mergeMap(({ id }) =>
          this.organizationsService.organizationsGetById({ organizationId: id }).pipe(
            map((org) =>
              OrganizationsActions.loadOrganizationByIdSuccess({
                organization: { data: org, loadedAt: new Date() }
              })
            ),
            catchError((error) => of(OrganizationsActions.loadOrganizationByIdFailure({ error }))),
            cancelOnSessionChange(this.actions$)
          )
        )
      )
    );

    this.createOrganization$ = createEffect(() =>
      this.actions$.pipe(
        ofType(OrganizationsActions.createOrganization),
        mergeMap(({ organization, project }) =>
          this.organizationsService.organizationsCreateWithProject({ body: { organization: organization.data, project: project.data } }).pipe(
            map((created) =>
              OrganizationsActions.createOrganizationSuccess({
                organization: {
                  data: created.organization!,
                  loadedAt: new Date()
                },
                project: {
                  data: created.project!,
                  loadedAt: new Date()
                }
              })
            ),
            catchError((error) => of(OrganizationsActions.createOrganizationFailure({ error }))),
            cancelOnSessionChange(this.actions$)
          )
        )
      )
    );

    this.updateOrganization$ = createEffect(() =>
      this.actions$.pipe(
        ofType(OrganizationsActions.updateOrganization),
        mergeMap(({ organizationId, changes }) =>
          this.organizationsService.organizationsUpdateById({
            organizationId: organizationId,
            body: changes as ApiDefOrganizationData
          }).pipe(
            map((updated) =>
              OrganizationsActions.updateOrganizationSuccess({
                organizationId: organizationId,
                organization: { data: updated, loadedAt: new Date() }
              })
            ),
            catchError((error) => of(OrganizationsActions.updateOrganizationFailure({ error }))),
            cancelOnSessionChange(this.actions$)
          )
        )
      )
    );

    this.deleteOrganization$ = createEffect(() =>
      this.actions$.pipe(
        ofType(OrganizationsActions.deleteOrganization),
        mergeMap(({ id }) =>
          this.organizationsService.organizationsDeleteById({ organizationId: id }).pipe(
            map(() => OrganizationsActions.deleteOrganizationSuccess({ id })),
            catchError((error) => of(OrganizationsActions.deleteOrganizationFailure({ error }))),
            cancelOnSessionChange(this.actions$)
          )
        )
      )
    );
  }
}